import React, { forwardRef, memo, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useFormContext } from 'react-hook-form';
import _get from 'lodash/get';
import _isEqual from 'lodash/isEqual';
import {
  FilterGroupingEnum,
  Site,
  SiteFilterInputObject,
  SitesSearchListQueryVariables,
  useSitesSearchListQuery,
} from 'graphql-common';
import {
  GetUrlParams,
  PAGINATION_PARAMS,
  PaginationParams,
} from '@lib/enums/urls';
import Chip, { ChipSizes, ChipTypes } from '@lib/components/Chip/Chip';
import LinkComponent, { LinkSizes, LinkTypes } from '@lib/components/Link/Link';
import { FloatingLabelProps } from '@lib/hocs/withFloatingLabel';
import { Values } from '@lib/interfaces/form';
import { SelectOption } from '@lib/components/Select/types';
import usePrevious from '@lib/hooks/usePrevious';
import MaterialIcon from '@lib/components/MaterialIcon/MaterialIcon';
import styles from './SiteChipListField.module.scss';

type Props = {
  fieldName?: string;
  ids?: string[];
  sectorId?: string;
  values: Values;
};

const SiteChipListField = forwardRef(
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  (props: Props & FloatingLabelProps, ref: React.ForwardedRef<unknown>) => {
    const { fieldName = '', ids, sectorId = '', values } = props;
    const prevIds = usePrevious(ids);
    const { setValue } = useFormContext();
    const { t } = useTranslation();
    const [paginationParams, setPaginationParams] =
      useState<PaginationParams>(PAGINATION_PARAMS);
    const [allItems, setAllItems] = useState<Site[]>([]);

    const page = _get(paginationParams, GetUrlParams.Page);
    const perPage = _get(paginationParams, GetUrlParams.PerPage);
    const orderingField = _get(paginationParams, GetUrlParams.OrderingField);
    const orderingDirection = _get(
      paginationParams,
      GetUrlParams.OrderingDirection,
    );

    const queryVariables: SitesSearchListQueryVariables = {
      page: Number(page),
      limit: Number(perPage),
    };

    const filters: SiteFilterInputObject[] = [];
    if (sectorId) {
      filters.push({
        sectorId: {
          grouping: FilterGroupingEnum.Or,
          predicate: { eq: sectorId },
        },
      });
    }
    if (filters.length) {
      queryVariables.filters = filters;
    }
    if (orderingField && orderingDirection) {
      queryVariables.sorts = {
        [orderingField]: orderingDirection,
      };
    }

    const query = useSitesSearchListQuery({
      fetchPolicy: 'no-cache',
      variables: queryVariables,
      skip: !sectorId,
    });

    const { loading, data, previousData, error } = query;
    const queryData = data || previousData;
    const firstLoading = loading && previousData === undefined;
    const newItems = _get(queryData, 'data.collection', []) as Site[];
    const metadata = _get(queryData, 'data.metadata');
    const totalPages = _get(metadata, 'totalPages', 1);
    const currentPage = _get(metadata, 'currentPage', 1);

    const handleLoadMore = () => {
      if (currentPage < totalPages) {
        setPaginationParams((prev) => ({
          ...prev,
          [GetUrlParams.Page]: `${currentPage + 1}`,
        }));
      }
    };

    const handleRemove = (id: string) => {
      const itemsValue = _get(values, fieldName, []) as SelectOption[];
      const newData = itemsValue.filter(
        ({ value: optionValue }: SelectOption) => optionValue !== id,
      );
      if (fieldName) {
        setValue(fieldName, newData);
      }
    };

    const formFieldValue = (_get(values, fieldName) || []) as SelectOption[];
    const visibleItems = allItems.filter((item) =>
      formFieldValue.some(({ value: optionValue }) => item.id === optionValue),
    );
    const visibleItemsIds = visibleItems.map((item) => item.id);
    let newFormFieldValue = formFieldValue;
    if (ids?.length) {
      newFormFieldValue = formFieldValue.filter(
        (item) => !ids.includes(item.value as string),
      );
    }
    if (visibleItemsIds.length) {
      newFormFieldValue = newFormFieldValue.filter(
        (item) => !visibleItemsIds.includes(item.value as string),
      );
    }

    useEffect(() => {
      if (newItems.length) {
        setAllItems((prevItems) => {
          const combined = [...prevItems, ...newItems];
          return combined.filter(
            (item, index, self) =>
              index === self.findIndex((subItem) => subItem.id === item.id),
          );
        });
      }
    }, [newItems, newItems.length]);

    useEffect(() => {
      if (prevIds === undefined && !_isEqual(ids, prevIds)) {
        setAllItems([]);
        query.refetch();
      }
    }, [ids, prevIds, query]);

    if (error || firstLoading) return null;

    const newFormFieldValueRender = newFormFieldValue?.length
      ? newFormFieldValue
          .filter(
            (formFieldItem) =>
              _get(formFieldItem, 'data.id') &&
              !visibleItems.some(
                (visibleItem) =>
                  _get(formFieldItem, 'data.id') === visibleItem.id,
              ),
          )
          .map((formFieldItem: SelectOption) => (
            <div key={formFieldItem.id}>
              <Chip
                type={ChipTypes.chipGray}
                size={ChipSizes.medium}
                label={formFieldItem.label}
                rightIconComponent={
                  <MaterialIcon
                    icon="cancel"
                    onClick={() => handleRemove(formFieldItem.value as string)}
                  />
                }
              />
            </div>
          ))
      : null;

    const visibleItemsRender = visibleItems?.length
      ? visibleItems.map((visibleItem) => (
          <div key={visibleItem.id}>
            <Chip
              type={ChipTypes.chipGray}
              size={ChipSizes.medium}
              label={visibleItem.name}
              rightIconComponent={
                <MaterialIcon
                  icon="cancel"
                  onClick={() => handleRemove(visibleItem.id)}
                />
              }
            />
          </div>
        ))
      : null;

    if (newFormFieldValueRender === null && visibleItemsRender === null)
      return null;

    return (
      <div>
        <div className={styles.chipsList}>
          {newFormFieldValueRender}
          {visibleItemsRender}
        </div>
        {currentPage < totalPages && (
          <LinkComponent
            size={LinkSizes.small}
            type={LinkTypes.secondaryGray}
            onClick={handleLoadMore}
          >
            {t('show-more')}
          </LinkComponent>
        )}
      </div>
    );
  },
);

export default memo(SiteChipListField);
